import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
  class: "w-full"
};
const _hoisted_2 = {
  class: "w-full border-2 border-dashed border-[#dcdfe6] rounded-md p-20px text-center hover:border-[#409eff]"
};
const _hoisted_3 = {
  class: "flex flex-col items-center justify-center py-20px"
};
const _hoisted_4 = {
  class: "el-upload__tip mt-10px text-[#909399] text-[12px]"
};
const _hoisted_5 = {
  key: 0,
  class: "mt-15px grid grid-cols-1 gap-2"
};
const _hoisted_6 = {
  class: "flex items-center"
};
const _hoisted_7 = {
  class: "text-[13px] text-[#303133] break-all"
};
const _hoisted_8 = {
  class: "flex justify-end w-full"
};
import { ref, computed, inject, getCurrentInstance, onMounted } from 'vue';
import { useMessage } from '@/hooks/web/useMessage';
import { useUpload } from '@/components/UploadFile/src/useUpload';
import { generateAcceptedFileTypes } from '@/utils';
import { Icon } from '@/components/Icon';
const maxFileSize = 15; // 最大文件大小(MB)
// 构建 accept 属性值，用于限制文件选择对话框中可见的文件类型
export default /*@__PURE__*/_defineComponent({
  __name: 'UploadStep',
  props: {
    modelValue: {
      type: Object,
      required: true
    }
  },
  emits: ['update:modelValue'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const formRef = ref(); // 表单引用
    const uploadRef = ref(); // 上传组件引用
    const parent = inject('parent', null); // 获取父组件实例
    const {
      uploadUrl,
      httpRequest
    } = useUpload(); // 使用上传组件的钩子
    const message = useMessage(); // 消息弹窗
    const fileList = ref([]); // 文件列表
    const uploadingCount = ref(0); // 上传中的文件数量
    // 支持的文件类型和大小限制
    const supportedFileTypes = ['TXT', 'MARKDOWN', 'MDX', 'PDF', 'HTML', 'XLSX', 'XLS', 'DOC', 'DOCX', 'CSV', 'EML', 'MSG', 'PPTX', 'XML', 'EPUB', 'PPT', 'MD', 'HTM'];
    const allowedExtensions = supportedFileTypes.map(ext => ext.toLowerCase()); // 小写的扩展名列表
    const acceptedFileTypes = computed(() => generateAcceptedFileTypes(supportedFileTypes));
    /** 表单数据 */
    const modelData = computed({
      get: () => {
        return props.modelValue;
      },
      set: val => emit('update:modelValue', val)
    });
    /** 确保 list 属性存在 */
    const ensureListExists = () => {
      if (!props.modelValue.list) {
        emit('update:modelValue', {
          ...props.modelValue,
          list: []
        });
      }
    };
    /** 是否所有文件都已上传完成 */
    const isAllUploaded = computed(() => {
      return modelData.value.list && modelData.value.list.length > 0 && uploadingCount.value === 0;
    });
    /**
     * 上传前检查文件类型和大小
     *
     * @param file 待上传的文件
     * @returns 是否允许上传
     */
    const beforeUpload = file => {
      // 1.1 检查文件扩展名
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.') + 1);
      if (!allowedExtensions.includes(fileExtension)) {
        message.error('不支持的文件类型！');
        return false;
      }
      // 1.2 检查文件大小
      if (!(file.size / 1024 / 1024 < maxFileSize)) {
        message.error(`文件大小不能超过 ${maxFileSize} MB！`);
        return false;
      }
      // 2. 增加上传中的文件计数
      uploadingCount.value++;
      return true;
    };
    /**
     * 文件上传成功处理
     *
     * @param response 上传响应
     * @param file 上传的文件
     */
    const handleUploadSuccess = (response, file) => {
      // 添加到文件列表
      if (response && response.data) {
        ensureListExists();
        emit('update:modelValue', {
          ...props.modelValue,
          list: [...props.modelValue.list, {
            name: file.name,
            url: response.data
          }]
        });
      } else {
        message.error(`文件 ${file.name} 上传失败`);
      }
      // 减少上传中的文件计数
      uploadingCount.value = Math.max(0, uploadingCount.value - 1);
    };
    /**
     * 文件上传失败处理
     *
     * @param error 错误信息
     * @param file 上传的文件
     */
    const handleUploadError = (error, file) => {
      message.error(`文件 ${file.name} 上传失败: ${error}`);
      // 减少上传中的文件计数
      uploadingCount.value = Math.max(0, uploadingCount.value - 1);
    };
    /**
     * 文件变更处理
     *
     * @param file 变更的文件
     */
    const handleFileChange = file => {
      if (file.status === 'success' || file.status === 'fail') {
        uploadingCount.value = Math.max(0, uploadingCount.value - 1);
      }
    };
    /**
     * 文件移除处理
     *
     * @param file 被移除的文件
     */
    const handleFileRemove = file => {
      if (file.status === 'uploading') {
        uploadingCount.value = Math.max(0, uploadingCount.value - 1);
      }
    };
    /**
     * 从列表中移除文件
     *
     * @param index 要移除的文件索引
     */
    const removeFile = index => {
      // 从列表中移除文件
      const newList = [...props.modelValue.list];
      newList.splice(index, 1);
      // 更新表单数据
      emit('update:modelValue', {
        ...props.modelValue,
        list: newList
      });
    };
    /** 下一步按钮处理 */
    const handleNextStep = () => {
      // 1.1 检查是否有文件上传
      if (!modelData.value.list || modelData.value.list.length === 0) {
        message.warning('请上传至少一个文件');
        return;
      }
      // 1.2 检查是否有文件正在上传
      if (uploadingCount.value > 0) {
        message.warning('请等待所有文件上传完成');
        return;
      }
      // 2. 获取父组件的goToNextStep方法
      const parentEl = parent || getCurrentInstance()?.parent;
      if (parentEl && typeof parentEl.exposed?.goToNextStep === 'function') {
        parentEl.exposed.goToNextStep();
      }
    };
    /** 初始化 */
    onMounted(() => {
      ensureListExists();
    });
    return (_ctx, _cache) => {
      const _component_el_upload = _resolveComponent("el-upload");
      const _component_el_button = _resolveComponent("el-button");
      const _component_el_form_item = _resolveComponent("el-form-item");
      const _component_el_form = _resolveComponent("el-form");
      return _openBlock(), _createBlock(_component_el_form, {
        ref_key: "formRef",
        ref: formRef,
        model: modelData.value,
        "label-width": "0",
        class: "mt-20px"
      }, {
        default: _withCtx(() => [_createVNode(_component_el_form_item, {
          class: "mb-20px"
        }, {
          default: _withCtx(() => [_createElementVNode("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_createVNode(_component_el_upload, {
            ref_key: "uploadRef",
            ref: uploadRef,
            class: "upload-demo",
            drag: "",
            action: _unref(uploadUrl),
            "auto-upload": true,
            "on-success": handleUploadSuccess,
            "on-error": handleUploadError,
            "on-change": handleFileChange,
            "on-remove": handleFileRemove,
            "before-upload": beforeUpload,
            "http-request": _unref(httpRequest),
            "file-list": fileList.value,
            multiple: true,
            "show-file-list": false,
            accept: acceptedFileTypes.value
          }, {
            default: _withCtx(() => [_createElementVNode("div", _hoisted_3, [_createVNode(_unref(Icon), {
              icon: "ep:upload-filled",
              class: "text-[48px] text-[#c0c4cc] mb-10px"
            }), _cache[0] || (_cache[0] = _createElementVNode("div", {
              class: "el-upload__text text-[16px] text-[#606266]"
            }, [_createTextVNode(" 拖拽文件至此，或者 "), _createElementVNode("em", {
              class: "text-[#409eff] not-italic cursor-pointer"
            }, "选择文件")], -1)), _createElementVNode("div", _hoisted_4, " 已支持 " + _toDisplayString(supportedFileTypes.join('、')) + "，每个文件不超过 " + _toDisplayString(maxFileSize) + " MB。 ", 1)])]),
            _: 1
          }, 8, ["action", "http-request", "file-list", "accept"])]), modelData.value.list && modelData.value.list.length > 0 ? (_openBlock(), _createElementBlock("div", _hoisted_5, [(_openBlock(true), _createElementBlock(_Fragment, null, _renderList(modelData.value.list, (file, index) => {
            return _openBlock(), _createElementBlock("div", {
              key: index,
              class: "flex justify-between items-center py-4px px-12px border-l-4 border-l-[#409eff] rounded-sm shadow-sm hover:bg-[#ecf5ff] transition-all duration-300"
            }, [_createElementVNode("div", _hoisted_6, [_createVNode(_unref(Icon), {
              icon: "ep:document",
              class: "mr-8px text-[#409eff]"
            }), _createElementVNode("span", _hoisted_7, _toDisplayString(file.name), 1)]), _createVNode(_component_el_button, {
              type: "danger",
              link: "",
              onClick: $event => removeFile(index),
              class: "ml-2"
            }, {
              default: _withCtx(() => [_createVNode(_unref(Icon), {
                icon: "ep:delete"
              })]),
              _: 2
            }, 1032, ["onClick"])]);
          }), 128))])) : _createCommentVNode("", true)])]),
          _: 1
        }), _createVNode(_component_el_form_item, null, {
          default: _withCtx(() => [_createElementVNode("div", _hoisted_8, [_createVNode(_component_el_button, {
            type: "primary",
            onClick: handleNextStep,
            disabled: !isAllUploaded.value
          }, {
            default: _withCtx(() => _cache[1] || (_cache[1] = [_createTextVNode(" 下一步 ")])),
            _: 1
          }, 8, ["disabled"])])]),
          _: 1
        })]),
        _: 1
      }, 8, ["model"]);
    };
  }
});